import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Truck } from "../../types/truck";
import { searchTrucksApi } from "../../api/trucksApi";
import type { RootState } from "../index";
import type { PaginationResponse } from "../../types/paginationResponse";

interface TrucksInGroupState {
  trucksByGroup: Record<string, Truck[]>;
  paginationByGroup: Record<string, PaginationResponse>;
  loadingStates: Record<string, boolean>;
  error: string | null;
}

const initialState: TrucksInGroupState = {
  trucksByGroup: {},
  paginationByGroup: {},
  loadingStates: {},
  error: null,
};

export const fetchTrucksInGroup = createAsyncThunk<
  { trucks: Truck[]; pagination: PaginationResponse },
  { groupId: string; page: number; pageSize: number },
  { rejectValue: string }
>("trucksInGroup/fetchTrucksInGroup", async ({ groupId, page, pageSize }, { rejectWithValue }) => {
  try {
    const response = await searchTrucksApi(
      { truckGroupId: groupId === "all" ? undefined : groupId },
      { page, pageSize },
    );
    return {
      trucks: response.trucks,
      pagination: response.pagination,
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch trucks in group");
  }
});

const trucksInGroupSlice = createSlice({
  name: "trucksInGroup",
  initialState,
  reducers: {
    clearTrucksInGroupError: (state) => {
      state.error = null;
    },
    updateTruckInGroup: (state, action: PayloadAction<{ groupId: string; truck: Truck }>) => {
      const { groupId, truck } = action.payload;
      if (state.trucksByGroup[groupId]) {
        const index = state.trucksByGroup[groupId].findIndex((t) => t._id === truck._id);
        if (index !== -1) {
          state.trucksByGroup[groupId][index] = truck;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrucksInGroup.pending, (state, action) => {
        state.loadingStates[action.meta.arg.groupId] = true;
        state.error = null;
      })
      .addCase(fetchTrucksInGroup.fulfilled, (state, action) => {
        const groupId = action.meta.arg.groupId;
        state.loadingStates[groupId] = false;
        state.trucksByGroup[groupId] = action.payload.trucks;
        state.paginationByGroup[groupId] = action.payload.pagination;
      })
      .addCase(fetchTrucksInGroup.rejected, (state, action) => {
        if (action.meta.arg) {
          state.loadingStates[action.meta.arg.groupId] = false;
        }
        state.error = action.payload || "Failed to fetch trucks in group";
      });
  },
});

// Selectors
export const selectTrucksInGroup = (state: RootState) => state.trucksInGroup.trucksByGroup;
export const selectTrucksInGroupLoadingStates = (state: RootState) =>
  state.trucksInGroup.loadingStates;
export const selectTrucksInGroupPagination = (state: RootState) =>
  state.trucksInGroup.paginationByGroup;
export const selectTrucksInGroupError = (state: RootState) => state.trucksInGroup.error;

export const selectTrucksForGroup = createSelector(
  [selectTrucksInGroup, (_state: RootState, groupId: string) => groupId],
  (trucksByGroup, groupId) => trucksByGroup[groupId] || [],
);

export const selectTrucksInGroupLoading = createSelector(
  [selectTrucksInGroupLoadingStates, (_state: RootState, groupId: string) => groupId],
  (loadingStates, groupId) => loadingStates[groupId] || false,
);

export const selectTrucksInGroupPaginationForGroup = createSelector(
  [selectTrucksInGroupPagination, (_state: RootState, groupId: string) => groupId],
  (paginationByGroup, groupId) => paginationByGroup[groupId],
);

export const { clearTrucksInGroupError, updateTruckInGroup } = trucksInGroupSlice.actions;
export default trucksInGroupSlice.reducer;
